import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: DialogSize;
  children: React.ReactNode;
  footer?: React.ReactNode;
  preventClose?: boolean;
  showCloseIcon?: boolean;
}

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  preventClose = false,
  showCloseIcon = true,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !preventClose) {
        console.log('[DIALOG] Closing dialog due to ESC key press');
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, preventClose]);

  // Handle clicking outside the dialog
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      console.log('[DIALOG] Click event detected, target:', event.target);

      if (
        dialogRef.current &&
        !dialogRef.current.contains(event.target as Node) &&
        isOpen &&
        !preventClose
      ) {
        console.log('[DIALOG] Closing dialog due to outside click');
        onClose();
      } else {
        console.log('[DIALOG] Click was inside dialog or preventClose is true');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, preventClose]);

  // Prevent scrolling when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('no-scrollbar');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('no-scrollbar');
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.classList.remove('no-scrollbar');
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Size maps for dialog width
  const sizeMap: Record<DialogSize, string> = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    full: 'max-w-full mx-4',
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div
        ref={dialogRef}
        className={`bg-white rounded-xl shadow-xl w-full ${sizeMap[size]} flex flex-col max-h-[90vh] overflow-hidden`}
      >
        {/* Dialog Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-gilroy-semibold text-gray-800">{title}</h3>
            {showCloseIcon && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // 🔥 prevents bubbling to parent dialog
                  onClose();
                }}
                className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                aria-label="Close dialog"
              >
                <X size={20} />
              </button>
            )}

          </div>
        )}

        {/* Dialog Content */}
        <div className="flex-1 px-6 py-4 overflow-y-auto no-scrollbar">
          {children}
        </div>

        {/* Dialog Footer (optional) */}
        {footer && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const DialogFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className = '',
  ...props
}) => {
  return (
    <div className={`flex justify-end space-x-2 px-6 py-4 border-t border-gray-200 bg-gray-50 ${className}`} {...props} />
  );
};

export default Dialog; 